/**
 * Date-range presets for the Custom tab.
 *
 * Lifted verbatim out of public/index.html. The only change is that `now` is an
 * argument with a default rather than a hard call to Date.now() — same result at
 * every call site, but the presets become testable without freezing the clock.
 *
 * PRESERVED QUIRK: "now" is UTC minus a FIXED four hours, so the app's idea of
 * "today" is EDT year-round and drifts by an hour through the winter. That is
 * how this has always behaved and how the server's week boundaries are already
 * aligned, so it is deliberately not fixed here — changing it would silently
 * move every reported figure by a day for some users. Fix it in one place, on
 * purpose, with the server, or not at all.
 */

export const EST_OFFSET_MS = 4 * 3600000;
export const DAY_MS = 86400000;

const iso = d => d.toISOString().slice(0, 10);

/**
 * The YYYY-MM-DD this app considers a timestamp to fall on.
 *
 * Exported so nothing else has to re-derive "what day is it here" — the fixed
 * offset above is the single definition, and everything that groups or labels by
 * day agrees with the date-range presets by construction.
 */
export function estDateKey(ts) {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : iso(new Date(d.getTime() - EST_OFFSET_MS));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "14 Jul 2026" from a YYYY-MM-DD string.
 *
 * Deliberately parses the string rather than going through Date: `new
 * Date('2026-07-14')` is UTC midnight, which renders as the 13th for anyone west
 * of Greenwich. Was archDate() in public/index.html.
 */
export function shortDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return s ? String(s) : '—';
  return `${parseInt(m[3], 10)} ${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
}

/** Hours and minutes, in the same fixed-offset zone as estDateKey. */
export function estTime(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const est = new Date(d.getTime() - EST_OFFSET_MS);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(est.getUTCHours())}:${pad(est.getUTCMinutes())}`;
}

/**
 * @param {string} preset  today | yesterday | last7 | last14 | last30 | thisWeek | lastWeek
 * @param {number} [now]   epoch ms; defaults to the real clock
 * @returns {{since: string, until: string}} inclusive ISO dates
 */
export function getPresetDates(preset, now = Date.now()) {
  const estNow = new Date(now - EST_OFFSET_MS);
  const today = iso(estNow);
  const yest  = iso(new Date(estNow - DAY_MS));

  if (preset === 'today')     return { since: today, until: today };
  if (preset === 'yesterday') return { since: yest,  until: yest };
  if (preset === 'last7')     return { since: iso(new Date(estNow -  6 * DAY_MS)), until: today };
  if (preset === 'last14')    return { since: iso(new Date(estNow - 13 * DAY_MS)), until: today };
  if (preset === 'last30')    return { since: iso(new Date(estNow - 29 * DAY_MS)), until: today };

  // Weeks run Monday–Sunday. getUTCDay() is 0 for Sunday, which is the END of
  // the current week here, not the start — hence the 6.
  if (preset === 'thisWeek') {
    const dow = estNow.getUTCDay();
    const diff = dow === 0 ? 6 : dow - 1;
    return { since: iso(new Date(estNow - diff * DAY_MS)), until: today };
  }
  if (preset === 'lastWeek') {
    const dow = estNow.getUTCDay();
    const diff = dow === 0 ? 6 : dow - 1;
    const monThis = new Date(estNow - diff * DAY_MS);
    const monLast = new Date(monThis - 7 * DAY_MS);
    const sunLast = new Date(monLast.getTime() + 6 * DAY_MS);
    return { since: iso(monLast), until: iso(sunLast) };
  }

  // Unknown preset falls back to last7 rather than throwing — a bad preset must
  // never blank the tab.
  return { since: iso(new Date(estNow - 6 * DAY_MS)), until: today };
}
